import {
	DateString,
	ListItem,
	PipeSeparator,
	ScrollableVirtualList,
	ScrollableVirtualListHandle,
	Space
} from "client/Components";
import { SelectableItemOptions, useUserTimezone } from "client/Hooks";
import {
	forwardRef,
	ReactElement,
	Ref,
	RefObject,
	useImperativeHandle,
	useMemo,
	useRef
} from "react";
import { DocumentListItem } from "../../DocumentListItem";
import {
	GridItem
} from "./SharePointRecentlyModified";
import { TimeZone } from "client/DateAndTime/timezones";
import { RecentDocumentAction, RecentDocumentActionContext } from "./actions";
import {
	formatFileLocation,
	getLocationModelFromDocument,
	SharePointItemLocationViewModel
} from "client/Features/Layout/TitleBar/GlobalSearch/Items/DocumentListItem";
import { RecentDocumentItemViewModel } from "client/Api/Documents/SharePoint/common";
import { DocumentNoResult } from "../../DocumentNoResult";
import { localize } from "client_legacy/ndaw/culture";
import { isNotNil } from "client/Misc/utils";

export type SharePointRecentlyModifiedListViewHandle = {
	resetScrollPosition: () => void;
};

export type SharePointRecentlyModifiedListViewProps = {
	items: readonly GridItem[];
	selectionActions: readonly RecentDocumentAction[];
	actionContext: RecentDocumentActionContext;
	isInSelectMode: boolean;
	hasSearch: boolean;
	selectionFor: (item: GridItem) => SelectableItemOptions;
	onOpenItem: (item: RecentDocumentItemViewModel) => void;
	onEnableSelectMode(): void;
};

export const SharePointRecentlyModifiedListView = forwardRef(
	(props: SharePointRecentlyModifiedListViewProps, ref: Ref<SharePointRecentlyModifiedListViewHandle>): ReactElement => {
		const {
			items,
			selectionActions,
			actionContext,
			isInSelectMode,
			hasSearch,
			selectionFor,
			onOpenItem,
			onEnableSelectMode
		} = props;

		const userTimezone: TimeZone = useUserTimezone();
		const listHandle: RefObject<ScrollableVirtualListHandle<GridItem> | null> = useRef<ScrollableVirtualListHandle<GridItem>>(null);

		useImperativeHandle(
			ref,
			() => ({
				resetScrollPosition: () => {
					listHandle.current
						?.scrollToTop();
				}
			}),
			[]
		);

		if (items.length === 0) {
			return (
				<DocumentNoResult
					hasSearch={hasSearch}
				/>
			);
		}

		return (
			<ScrollableVirtualList
				trackingId="recent-document-list-view"
				ref={listHandle}
				items={items}
				estimatedHeightOfAnItem={64}
				itemKey={getUniqueKey}
				renderAfterItems={
					<ScrollableVirtualList.MobilePlusMenuSpacing />
				}
			>
				{(x: GridItem): ReactElement => (
					<DocumentListItem
						name={x.name}
						additionalInformation={(
							<AdditionalInformation
								document={x.model}
								timezone={userTimezone}
							/>
						)}
						documentKind={x.model.itemType}
						extension={x.extension}
						isInSelectMode={isInSelectMode}
						onEnableSelectMode={onEnableSelectMode}
						selectable={selectionFor(x)}
						onOpenItem={() => onOpenItem(x.model)}
						actions={selectionActions}
						actionContext={{ ...actionContext, selection: [x.model] }}
					/>
				)}
			</ScrollableVirtualList>
		);
	}
);
SharePointRecentlyModifiedListView.displayName = "SharePointRecentlyModifiedListView";

function getUniqueKey(item: GridItem): string {
	return item.uniqueKey;
}

type AdditionalInformationProps = {
	document: RecentDocumentItemViewModel;
	timezone: TimeZone;
};

const AdditionalInformation = (props: AdditionalInformationProps): ReactElement | null => {
	const {
		document,
		timezone
	} = props;

	const locationModel = useMemo(
		(): SharePointItemLocationViewModel =>
			getLocationModelFromDocument(document),
		[document]
	);

	return (
		<>
			<ListItem.SubTitleIcon kind="pen" />
			{isNotNil(document.modifiedOn) && (
				<>
					<Space />
					<DateString
						value={document.modifiedOn}
						format="RelativeTimestamp"
						timezone={timezone}
					/>
				</>
			)}
			{isNotNil(document.modifiedBy) && (
				<>
					<PipeSeparator />
					{localize("_(Edited)By").toLowerCase()}
					<Space />
					{document.modifiedBy}
				</>
			)}
			{locationModel.numberOfSegments > 0 && (
				<>
					<PipeSeparator />
					{formatFileLocation(locationModel)}
				</>
			)}
		</>
	);
};
AdditionalInformation.displayName = "AdditionalInformation";
